// Welche Payments eines Partners was steuern. Rein, ohne React und ohne
// helpers/index.js (das zieht aws-amplify und bricht Jest).
//
// Migration heißt, CS legt im Aivy OS ein neues Payment mit plan an und das
// alte bleibt. Daraus ergeben sich vier Rollen.
// periodPayment ist das neueste Nicht-Credit-Payment wie bisher newestPayment
// in payment-context.js. Es steuert accessExpired und den Payment-Alert.
// legacyPayment ist das neueste ohne Plan, planPayment das neueste mit Plan.
// legacyModePayment liefert die alten Gates, solange der Schalter aus ist.
import { isNumber } from 'lodash'

import { PLANS, normalizePlanId, presetProducts } from './plans'

export const IGNORED_PLAN_REASONS = {
  UNKNOWN_PLAN: 'UNKNOWN_PLAN',
  TRIAL_WITH_PLAN: 'TRIAL_WITH_PLAN'
}

// Wie bisher in payment-context.js, Credit-Payments tragen
// unused_campaign_credits und zählen nie als Laufzeit-Payment
export const isCreditPayment = (payment) =>
  isNumber(payment?.unused_campaign_credits)

// Rohwert des Plans. payment.plan gewinnt, damit eine Simulation nicht vom
// echten Wert aus der Plan-Query überschrieben wird. Payment.plan steht nie in
// der Payment-Query (src/graphql/schema/payment.js), echte Werte kommen über
// planById aus der eigenen Plan-Query.
const rawPlanOf = (payment, planById) =>
  payment?.plan ?? (payment?.id != null ? planById?.[payment.id] : undefined)

// Normalisierter Plan oder null. Ein Plan an einem Trial wird ignoriert, ein
// unbekannter Wert gilt als legacy (fail-open).
export const planOf = (payment, planById = {}) => {
  if (!payment || payment.is_trial) return null
  return normalizePlanId(rawPlanOf(payment, planById))
}

const ignoredReasonOf = (payment, planById) => {
  const raw = rawPlanOf(payment, planById)
  if (raw === null || raw === undefined || raw === '') return null
  if (payment.is_trial) return IGNORED_PLAN_REASONS.TRIAL_WITH_PLAN
  if (normalizePlanId(raw) === null) return IGNORED_PLAN_REASONS.UNKNOWN_PLAN
  return null
}

const lastIndexWhere = (list, predicate) => {
  for (let i = list.length - 1; i >= 0; i--) if (predicate(list[i])) return i
  return -1
}

// ignored listet Payments mit einem Plan-Wert, der nicht zählt, damit der
// Aufrufer einmal an Bugsnag melden kann.
export const selectPayments = (payments, planById = {}) => {
  // Filter und Sortierung zeichengleich zu payment-context.js. Array.sort ist
  // stabil, bei gleichem createdAt gewinnt also wie bisher das spätere
  // Element der Eingabe.
  const candidates = (payments || [])
    .filter((payment) => !isCreditPayment(payment))
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))

  const planIds = candidates.map((payment) => planOf(payment, planById))
  const legacyIndex = lastIndexWhere(planIds, (planId) => planId === null)
  const planIndex = lastIndexWhere(planIds, (planId) => planId !== null)

  const periodPayment = candidates[candidates.length - 1] || null
  const legacyPayment = legacyIndex >= 0 ? candidates[legacyIndex] : null
  const planPayment = planIndex >= 0 ? candidates[planIndex] : null

  // Ohne Legacy-Payment rechnet das Plan-Payment mit alter Gate-Logik. Ein
  // Trial, auf das ein neueres Plan-Payment folgt, ist ein konvertierter Trial,
  // dann gilt ebenfalls das Plan-Payment.
  const legacyModePayment =
    !legacyPayment ||
    (legacyPayment.is_trial && planPayment && planIndex > legacyIndex)
      ? planPayment
      : legacyPayment

  const ignored = candidates
    .map((payment) => ({ payment, reason: ignoredReasonOf(payment, planById) }))
    .filter(({ reason }) => reason !== null)

  return {
    candidates,
    periodPayment,
    legacyPayment,
    planPayment,
    legacyModePayment,
    ignored
  }
}

// Admin-Simulation. Baut aus einem echten Payment ein Plan-Payment mit den
// Preset-Produkten. Die Mengen bleiben null, damit die Paket-Defaults greifen.
// Braucht kein Schema und berührt die Laufzeit (periodPayment) nie.
export const simulatePlanPayment = ({ base, planId }) => {
  if (!PLANS[planId]) return null

  return {
    ...base,
    plan: planId,
    is_trial: false,
    products: presetProducts(planId),
    career_count: null,
    seat_count: null,
    spaces_count: null,
    __simulated: true
  }
}
